import { DiscountType, InventoryItemStatus } from '@trnsact/trnsact-shared-types';
import { Nullable } from 'global';
import { InventoryEquipment } from 'modules/inventory';
import { formatStringToNumber } from 'utils/formatters';
import { Equipment, EquipmentFormValue } from '../types';
import { calcAttachmentTotals } from '../lib/charges';

type InventoryToFormFields = Pick<
  EquipmentFormValue,
  'year' | 'make' | 'model' | 'commonInfo' | 'calcPricing' | 'attachments'
>;

interface RawAttachment {
  type: string;
  model: string;
  unitCost: string | number;
  retailPrice: Nullable<number>;
  category: string;
  manufacturer: string;
  quantityPerAsset: number;
  discountValue: Nullable<number>;
  discountType: Nullable<DiscountType>;
}

export class EquipmentFormMapper {
  static fromInventoryItem(item: InventoryEquipment): InventoryToFormFields {
    const { details, price, id, attachments } = item.equipment;

    const retailPrice = price.retailPrice ?? 0;
    const wholesaleCost = price.wholesaleCost ?? 0;

    const mappedAttachments = this.mapAttachments(attachments);

    return {
      year: details.year || null,
      make: details.make || null,
      model: details.model || null,
      attachments: mappedAttachments,
      commonInfo: {
        vin: details.vin || '',
        class: details.class || null,
        stockId: details.stockId || '',
        condition: details.condition || '',
        description: details.description || '',
        mileage: details.mileage ? Number(details.mileage) : null,
        hours: null,
        quantity: 1,
        retailPrice,
        saleCost: wholesaleCost,
        wholeSaleCost: wholesaleCost,
        inventoryItem: {
          inventoryId: item.id,
          status: InventoryItemStatus.Unknown,
          inventoryOption: {
            value: id,
            label: details.name,
          },
        },
      },
      calcPricing: this.buildCalcPricing(retailPrice, wholesaleCost, mappedAttachments),
    };
  }

  static fromEquipmentEntity(equipment: Equipment): EquipmentFormValue {
    const commonInfo: EquipmentFormValue['commonInfo'] = {
      class: equipment.classType,
      stockId: equipment.stockId,
      mileage: equipment.mileage,
      hours: equipment.hours ?? null,
      quantity: equipment.quantity,
      condition: equipment.newUsed,
      vin: equipment.serialNumberVin,
      saleCost: equipment?.wholesaleCost ?? 0,
      retailPrice: equipment?.retailPrice ?? 0,
      wholeSaleCost: equipment?.wholesaleCost ?? 0,
      description: equipment.additionalDescription,
      inventoryItem: {
        inventoryOption: null,
        inventoryId: equipment.inventoryItem?.inventoryId ?? '',
        status: equipment.inventoryItem?.status ?? InventoryItemStatus.Unknown,
      },
    };

    const mappedAttachments = this.mapAttachments(equipment?.attachments ?? []);

    return {
      year: equipment.year ?? null,
      make: equipment.make ?? null,
      model: equipment.model ?? null,
      modelBase: equipment.modelBase ?? null,
      commonInfo,
      attachments: mappedAttachments,
      calcPricing: this.buildCalcPricing(commonInfo.retailPrice, commonInfo.saleCost, mappedAttachments),
    };
  }

  private static mapAttachments(attachments: RawAttachment[]): EquipmentFormValue['attachments'] {
    return attachments.map(attachment => ({
      ...attachment,
      retailPrice: attachment?.retailPrice ?? 0,
      discountValue: attachment?.discountValue ?? 0,
      unitCost: formatStringToNumber(attachment.unitCost) ?? 0,
      discountType: attachment?.discountType ?? DiscountType.Flat,
    }));
  }

  private static buildCalcPricing(
    retailPrice: number,
    wholesaleCost: number,
    attachments: EquipmentFormValue['attachments']
  ): EquipmentFormValue['calcPricing'] {
    const attachmentPrices = calcAttachmentTotals(attachments);

    return {
      retail: {
        attachments: attachmentPrices.retailPrice,
        total: retailPrice + attachmentPrices.retailPrice,
      },
      wholesale: {
        attachments: attachmentPrices.wholesaleCost,
        total: wholesaleCost + attachmentPrices.wholesaleCost,
      },
      customer: {
        attachments: attachmentPrices.customerPriceWithDiscount,
        equipment: retailPrice,
        total: retailPrice + attachmentPrices.customerPriceWithDiscount,
      },
    };
  }
}
